import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// Language configuration with flags
export const languages = [
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', flagAlt: 'SE' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', flagAlt: 'NO' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', flagAlt: 'DK' },
  { code: 'en', name: 'English', flag: '🇬🇧', flagAlt: 'GB' },
  { code: 'es', name: 'Español', flag: '🇪🇸', flagAlt: 'ES' },
];

// Flag component
const FlagIcon = ({ flag, flagAlt, size = "text-lg" }) => (
  <span className={`${size} leading-none`} role="img" aria-label={flagAlt}>
    {flag}
  </span>
);

// Compact language selector for auth pages
export const LanguageSelectorCompact = ({ className = "" }) => {
  const { i18n, t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('yubi_language') || i18n.language || 'sv';
  });

  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];

  const handleLanguageChange = (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('yubi_language', langCode);
    i18n.changeLanguage(langCode);
    const lang = languages.find(l => l.code === langCode);
    toast.success(`${t('language.changed')} ${lang?.name || langCode}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/10 transition-colors border border-slate-600 ${className}`}
          data-testid="language-selector-auth"
        >
          <Globe size={16} />
          <FlagIcon flag={currentLang.flag} flagAlt={currentLang.flagAlt} />
          <span className="hidden sm:inline">{currentLang.name}</span>
          <ChevronDown size={14} className="opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex items-center justify-between cursor-pointer ${
              currentLanguage === lang.code ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
            }`}
            data-testid={`lang-auth-${lang.code}`}
          >
            <span className="flex items-center gap-2">
              <FlagIcon flag={lang.flag} flagAlt={lang.flagAlt} size="text-base" />
              <span>{lang.name}</span>
            </span>
            {currentLanguage === lang.code && (
              <span className="text-indigo-600 dark:text-indigo-400 text-xs font-medium">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelectorCompact;
